import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import {
  Account,
  ActivityFilterModel,
  ActivityOrderBy,
  Issue,
  IssueMessageType,
  replaceMentionPlaceholderWithNames,
  Severity,
  useTranslatedText,
} from '@gripp/shared-logic';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes';
import { getIssueAuditData, getIssueFullName } from '../issue/issueFormatUtils';
import { IssuesSeverityIcon } from '../shared/IssuesSeverityIcons';

export interface IssueCardData {
  type: string;
  asset: {
    name: string;
  };
  workspace: {
    name: string;
  };
  id: string;
  title: string;
  subtitle?: string;
  severity: Severity;
  createdAt: string;
  effectiveCreationDate?: string;
  isPinned?: boolean;
  updatedAt: string;
  labels?: {
    id: string;
    name: string;
  }[];
  nextReminderDate?: string;
  userMentions?: Account[];
  latestMessage: {
    text: string;
    createdBy: Account;
    userMentions: Account[];
    type: IssueMessageType;
  };
}

export type IssueCardProps = {
  account: Account | null
  activity: IssueCardData;
  showAssetName?: boolean;
  isUnread?: boolean;
  isMentionedInInbox?: boolean;
  filter: ActivityFilterModel;
};

export const IssueCard = (props: IssueCardProps) => {
  const { t } = useTranslation();
  const { activity } = props;
  const [auditData, setAuditData] = useState({ who: '...', when: '...' });
  const [isHovered, setIsHovered] = useState(false);

  const isMentioned = useMemo(() => {
    return props.isMentionedInInbox ?? false;
  }, [props.isMentionedInInbox]);

  useEffect(() => {
    setAuditData(
      getIssueAuditData(activity as unknown as Issue, props.filter.orderBy as ActivityOrderBy)
    );
  }, [activity, props.filter.orderBy]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const getProcessedTitle = () => {
    const titleText = activity.title;
    
    // Process mentions to ensure clean format
    if (titleText.includes('@[') && (activity as any).userMentions) {
      return replaceMentionPlaceholderWithNames(
        titleText,
        (activity as any).userMentions,
        t('mentions.unknownUser'),
        true
      );
    }
    
    return titleText;
  };

  const getLatestMessageText = () => {
    const latestMessage = activity?.latestMessage;

    if (!latestMessage) {
      return '';
    }

    const user = getIssueFullName(
      latestMessage.createdBy?.id,
      latestMessage.createdBy?.name
    );
    
    // Show "you are mentioned" for unread inbox items where user is mentioned
    if (props.isUnread && props.isMentionedInInbox) {
      return <Text style={{color: Colors.primary, fontSize: 14}}>{t('activity.list.youAreMentioned')}</Text>;
    }

    if (
      latestMessage.type === IssueMessageType.RoutineMessage ||
      latestMessage.type === IssueMessageType.RoutineExceptionMessage
    ) {
      return activity?.subtitle
        ? `${user} ${activity.subtitle}`
        : t('routines.message.messageTitle', { name: user }).slice(0, -1);
    }

    if (
      latestMessage.type === IssueMessageType.SystemMessage ||
      latestMessage.type === IssueMessageType.RoutinePromptMessage
    ) {
      const { translatedText } = useTranslatedText('text', latestMessage);
      return translatedText;
    }

    if (!latestMessage.text) {
      return `${user}: ${t('issue.latestMessage.media')}`;
    }

    const { translatedText } = useTranslatedText('text', latestMessage);
    const messageText = translatedText || latestMessage?.text;
    
    // Process mentions to ensure clean format
    const processedText = messageText.includes('@[') 
      ? replaceMentionPlaceholderWithNames(
          messageText,
          latestMessage?.userMentions,
          t('mentions.unknownUser'),
          true
        )
      : messageText;
    
    return `${user}: ${processedText}`;
  };

  const hoverProps =
    Platform.OS === 'web'
      ? {
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
        }
      : {};

  return (
    <View style={styles.card} {...hoverProps}>
      <View style={[styles.row, { justifyContent: 'space-between' }]}>
        <View style={[styles.row, { flex: 1 }]}>
          <IssuesSeverityIcon severity={activity.severity} />
          <View
            style={[
              styles.row,
              styles.subHeaderContainer,
              isHovered && { backgroundColor: Colors.white },
              {
                ...Platform.select({
                  web: { maxWidth: '95%' },
                  default: { maxWidth: '90%' },
                }),
              },
            ]}
          >
            <MaterialCommunityIcons
              name="qrcode"
              color={Colors.grayText}
              size={14}
            />
            <Text
              variant="bodyMedium"
              style={[styles.detailText, styles.details]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {activity.asset?.name || activity.workspace.name}
            </Text>

          </View>
        </View>

        <View
          style={[
            styles.row,
            styles.whenBg
          ]}
        >
          <Text
            variant="bodyMedium"
            style={styles.detailText}
          >
            {auditData.when}
          </Text>
        </View>
      </View>

      <View style={styles.content}>
        <View style={styles.row}>
          {props.filter.orderBy === ActivityOrderBy.ReminderDate && activity.nextReminderDate && (
            <MaterialIcons
              name="alarm-on"
              size={16}
              style={{ color: Colors.grayText, paddingRight: 8 }}
            />
          )}
          <Text style={styles.title} numberOfLines={1}>
            {getProcessedTitle()}
          </Text>
          
          {/* Pinned icon positioned to the right of title */}
            <View style={styles.indicatorIconContainer}>
              {activity.isPinned && (
                  <MaterialCommunityIcons
                    name="pin"
                    color={Colors.black}
                    size={16}
                    style={styles.pinnedIcon}
                  />
              )}

              {/* Orange unread dot indicator for unread messages */}
              {
                props.isUnread && (
                  <View style={styles.unreadDot}></View>
                )
              }
            </View>
        </View>
        <View style={styles.row}>
          <Text
            variant="bodyMedium"
            style={styles.lastMessage}
            numberOfLines={1}
          >
            {getLatestMessageText()}
          </Text>
        </View>

        {activity.labels && activity.labels.length > 0 && (
          <View style={styles.labelsContainer}>
            {activity.labels.map((label) => (
              <View key={label.id} style={styles.labelPill}>
                <Text style={styles.labelText}>{label.name}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 0,
    width: '100%',
    alignSelf: 'stretch',
    padding: 16,
    height: 'auto',
    ...Platform.select({
      web: { gap: 12 },
      default: {
        gap: 10,
      },
    }),
  },
  content: {
    flexDirection: 'column',
    ...Platform.select({
      web: { gap: 4 },
      default: {
        gap: 2,
      },
    }),
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    fontSize: 16,
    color: Colors.black,
    fontWeight: '500',
    flex: 1,
    marginRight: 8,
  },
  whenBg: {
    paddingHorizontal: 8,
    borderRadius: 100,
    zIndex: 2,
    marginLeft: 20,
  },
  details: {
    color: Colors.grayText,
  },
  detailsWhite: {
    color: Colors.white,
  },
  detailText: {
    fontSize: 14,
    fontWeight: '500',
    letterSpacing: -0.4,
    lineHeight: 16,
  },
  subHeaderContainer: {
    overflow: 'hidden',
    backgroundColor: Colors.primaryGrayBackground,
    paddingHorizontal: 4,
    borderRadius: 4,
    gap: 4,
    height: 20,
  },
  lastMessage: {
    fontSize: 16,
    fontWeight: '400',
    color: Colors.grayText,
  },
  indicatorIconContainer: {
    marginLeft: 'auto',
    alignItems: 'center',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'row',
    gap: 5,
  },
  pinnedIcon: {
    width: 16,
    height: 16,
    backfaceVisibility: 'hidden',
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: Colors.primary,
    zIndex: 10,
    marginRight: 5
  },  
  labelsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4,
  },
  labelPill: {
    backgroundColor: Colors.primaryGrayBackground,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 15,
    alignSelf: 'flex-start',
  },
  labelText: {
    fontSize: 14,
    lineHeight: 16,
    color: Colors.black,
    fontWeight: '400',
  },
  mentionContainer: {
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  mentionText: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.primary,
  },
  mentionDot: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: [{ translateY: -4 }],
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Colors.primary,
    zIndex: 1,
  },
});
